import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Onboarding.css';

// ============================================================
//  🔽🔽🔽  CONFIGURATION - Replace with your images  🔽🔽🔽
// ============================================================

const IMAGE_CONFIG = {
  logo: '/images/board-1.png',
  slide1: '/images/board-2.png',
  slide2: '/images/board-3.png',
  slide3: '/images/board-4.png',
};

// ============================================================
//  🔼🔼🔼  END CONFIGURATION  🔼🔼🔼
// ============================================================

// ===== IMAGE COMPONENT WITH FALLBACK =====
const Image = ({ src, alt, className, fallback }) => {
  const [error, setError] = useState(false);
  if (error || !src) {
    return <div className={`${className}--fallback`}>{fallback || '✦'}</div>;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setError(true)}
      loading="lazy"
      draggable={false}
    />
  );
};

const Onboarding = ({ onComplete, onLogin, onSignup }) => {
  const [current, setCurrent] = useState(0);
  const [showSplash, setShowSplash] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const totalSlides = 3;
  const navigate = useNavigate();
  const autoTimer = useRef(null);

  // ===== SPLASH TIMER =====
  useEffect(() => {
    const t = setTimeout(() => {
      setShowSplash(false);
      setIsReady(true);
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  // ===== AUTO-PLAY =====
  const startAuto = useCallback(() => {
    if (autoTimer.current) clearInterval(autoTimer.current);
    autoTimer.current = setInterval(() => {
      if (current < totalSlides - 1) setCurrent((prev) => prev + 1);
      else clearInterval(autoTimer.current);
    }, 5000);
  }, [current, totalSlides]);

  const stopAuto = useCallback(() => {
    if (autoTimer.current) {
      clearInterval(autoTimer.current);
      autoTimer.current = null;
    }
  }, []);

  useEffect(() => {
    if (isReady) startAuto();
    return () => stopAuto();
  }, [isReady, startAuto, stopAuto]);

  // ===== NAVIGATION =====
  const goTo = useCallback(
    (idx) => {
      if (idx < 0 || idx >= totalSlides) return;
      setCurrent(idx);
      stopAuto();
      startAuto();
    },
    [totalSlides, stopAuto, startAuto]
  );

  const next = useCallback(() => {
    if (current < totalSlides - 1) {
      setCurrent((prev) => prev + 1);
      stopAuto();
      startAuto();
    }
  }, [current, totalSlides, stopAuto, startAuto]);

  const prev = useCallback(() => {
    if (current > 0) {
      setCurrent((prev) => prev - 1);
      stopAuto();
      startAuto();
    }
  }, [current, stopAuto, startAuto]);

  const skipToEnd = useCallback(() => {
    setCurrent(totalSlides - 1);
    stopAuto();
  }, [totalSlides, stopAuto]);

  // ===== TOUCH EVENTS =====
  const handleTouchStart = useCallback((e) => {
    setTouchStart(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.touches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) > 50) {
      if (diff > 0) next();
      else prev();
    }
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, next, prev]);

  // ===== ACTIONS =====
  const handleSignup = useCallback(() => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    if (onSignup) {
      onSignup();
    } else {
      navigate('/signup');
    }
  }, [onSignup, navigate]);

  const handleLogin = useCallback(() => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    if (onLogin) {
      onLogin();
    } else {
      navigate('/login');
    }
  }, [onLogin, navigate]);

  // ===== SLIDE DATA =====
  const slides = [
    {
      image: IMAGE_CONFIG.slide1,
      title: 'یادگیری هوشمند',
      highlight: 'شخصی‌سازی‌شده',
      desc: 'یادو با تحلیل داده‌های یادگیری‌ت، مسیری منحصربه‌فرد برای پیشرفت طراحی می‌کند که کاملاً با تو هماهنگ است.',
      tags: ['🧠 هوش مصنوعی', '📊 تحلیل داده', '🚀 پیشرفت'],
      stat: '۹۸٪',
      statLabel: 'رضایت کاربران',
      statColor: '#34C759',
    },
    {
      image: IMAGE_CONFIG.slide2,
      title: 'چالش‌های روزانه',
      highlight: 'هر روز یک ماجرا',
      desc: 'با چالش‌های جدید روزانه، مهارت‌هایت را محک بزن، ستاره جمع کن و رتبه‌ات را در بین کاربران بالا ببر.',
      rewards: ['⭐', '🏅', '🎁'],
      stat: '۱۲۰+',
      statLabel: 'چالش فعال',
      statColor: '#F5A623',
    },
    {
      image: IMAGE_CONFIG.slide3,
      title: 'همراه همیشگی تا موفقیت',
      highlight: '۲۴ ساعت کنار تو',
      desc: 'یادو نه تنها به سوالاتت پاسخ می‌دهد، بلکه با انگیزه‌بخشی هوشمند، تو را به اهداف بزرگ‌تر می‌رساند.',
      message: '💬 «هر لحظه، هر سوال، من هستم»',
      stat: '۲۴ ساعت',
      statLabel: 'پشتیبانی هوشمند',
      statColor: '#5B7FFF',
    },
  ];

  // ===== RENDER SLIDE =====
  const renderSlide = () => {
    const data = slides[current];
    if (!data) return null;

    return (
      <div className="slide active">
        <div className="slide__media">
          <div className="slide__media-glow" />
          <Image src={data.image} alt={data.title} className="slide__img" fallback="✦" />
        </div>

        <div className="slide__content">
          <div className="slide__badge">
            <span className="badge__text">✦ نسخه ۲۰۲۶</span>
          </div>

          <h2 className="slide__title">
            {data.title}
            <br />
            <span className="accent">{data.highlight}</span>
          </h2>

          <p className="slide__desc">{data.desc}</p>

          {data.tags && (
            <div className="slide__tags">
              {data.tags.map((tag, i) => (
                <span key={i}>{tag}</span>
              ))}
            </div>
          )}

          {data.rewards && (
            <div className="rewards">
              {data.rewards.map((emoji, i) => (
                <div className="reward" key={i}>
                  <div className="reward__circle">{emoji}</div>
                </div>
              ))}
            </div>
          )}

          {data.message && (
            <div className="message">
              <span className="highlight">{data.message}</span>
            </div>
          )}

          <div className="slide__stat" style={{ borderColor: data.statColor + '33' }}>
            <span className="stat__number" style={{ color: data.statColor }}>
              {data.stat}
            </span>
            <span className="stat__label">{data.statLabel}</span>
          </div>
        </div>
      </div>
    );
  };

  // ===== RENDER BOTTOM =====
  const renderBottom = () => {
    const isLast = current === totalSlides - 1;

    return (
      <div className="bottom">
        <div className="dots">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <span
              key={i}
              className={`dot ${i === current ? 'active' : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
          <div className="dots__progress" style={{ width: `${((current + 1) / totalSlides) * 100}%` }} />
        </div>

        {!isLast && (
          <button className="btn btn--next" onClick={next}>
            <span className="btn__text">ادامه مسیر</span>
            <span className="btn__arrow">→</span>
          </button>
        )}

        {isLast && (
          <div className="auth-group">
            <button className="btn btn--primary" onClick={handleSignup}>
              <span className="btn__icon">✦</span>
              <span className="btn__text">شروع ثبت نام</span>
            </button>
            <button className="btn btn--secondary" onClick={handleLogin}>
              <span className="btn__icon">◈</span>
              <span className="btn__text">ورود به حساب</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // ===== MAIN RENDER =====
  return (
    <div className="onboarding-page">
      {/* BACKGROUND */}
      <div className="bg-art">
        <div className="orb orb--1" />
        <div className="orb orb--2" />
        <div className="orb orb--3" />
        <div className="bg-gradient" />
      </div>

      {/* SPLASH SCREEN */}
      <div className={`splash ${showSplash ? '' : 'hide'}`}>
        <div className="splash__container">
          <div className="splash__icon">
            <Image src={IMAGE_CONFIG.logo} alt="یادو" className="splash__icon" fallback="✦" />
          </div>
          <h1 className="splash__title">
            <span className="gradient-text">یادو</span>
          </h1>
          <p className="splash__sub">افق‌های نوین یادگیری</p>
          <div className="splash__loader">
            <div className="loader__dot" />
            <div className="loader__dot" />
            <div className="loader__dot" />
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className={`main-wrapper ${isReady ? 'ready' : ''}`}>
        <header className="header">
          <div className="logo">
            <div className="logo__icon">
              <Image src={IMAGE_CONFIG.logo} alt="یادو" className="logo__icon" fallback="✦" />
            </div>
            <div className="logo__text">
              <h2 className="gradient-text">یادو</h2>
              <span>هوش مصنوعی</span>
            </div>
          </div>
          <button className="btn--skip" onClick={skipToEnd}>
            رد کردن
          </button>
        </header>

        <div
          className="slides"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {renderSlide()}
        </div>

        {renderBottom()}
      </div>
    </div>
  );
};

export default Onboarding;